import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import topForms from "@/lib/topForms.json";
import { Landmark, ArrowRight, Sparkles, ExternalLink, MapPin, HelpCircle, FileText, CheckCircle2 } from "lucide-react";
import { usePlaceLang, altLinks } from "@/lib/placeLang";
import { LocalLawyers } from "@/components/LocalLawyers";
import { pageText } from "@/lib/i18n/pages";
import { trackView } from "@/lib/trackView";
import { PLACE_SEO } from "@/lib/i18n/placeSeo";

const SITE = typeof window !== "undefined" ? window.location.origin : "";
const KIND_ES = { City: "Ciudad", Town: "Pueblo", Village: "Villa" };

const KIND_TR = {
  fr: { City: "Ville", Town: "Commune", Village: "Village" },
  ar: { City: "مدينة", Town: "بلدة", Village: "قرية" },
  hmn: { City: "Lub nroog", Town: "Lub zos", Village: "Lub zos me" },
};

const T = {
  en: {
    loading: "Loading…", nc: "North Carolina", county: (c) => `${c} County`, kind: (type, c) => `${type} in ${c} County`,
    h1: (n) => `${n}, North Carolina Court Forms`, find: "Find my forms with AI", browse: "Browse all forms",
    tips: (n) => `Filing tips for ${n} residents`, where: (n) => `Where ${n} cases are filed`,
    whereBody: (d) => `${d.name} is in ${d.county} County, so most court forms are filed with the Clerk of Superior Court in ${d.county_seat}.`,
    extends: (d) => ` Parts of ${d.name} extend into ${d.other_counties.join(", ")} County — file in the county where you live or where the case arose.`,
    confirm: " Confirm fees, e-filing and the courthouse address with the NC Judicial Branch before you go.",
    courthouse: (c) => `${c} County courthouse on nccourts.gov`, popular: (n) => `Popular forms for ${n} residents`,
    faq: (n) => `${n} filing questions`, nearby: (c) => `Other places in ${c} County`, allCounty: (c) => `All of ${c} County →`,
    disclaimer: (n) => `NC Legal Forms is not affiliated with the North Carolina Judicial Branch or the Town/City of ${n} and does not provide legal advice.`,
  },
  es: {
    loading: "Cargando…", nc: "Carolina del Norte", county: (c) => `Condado de ${c}`, kind: (type, c) => `${KIND_ES[type] || type} en el condado de ${c}`,
    h1: (n) => `Formularios judiciales de ${n}, Carolina del Norte`, find: "Encontrar mis formularios con IA", browse: "Ver todos los formularios",
    tips: (n) => `Consejos para residentes de ${n}`, where: (n) => `Dónde se presentan los casos de ${n}`,
    whereBody: (d) => `${d.name} está en el condado de ${d.county}, por lo que la mayoría de los formularios se presentan ante el Secretario del Tribunal Superior en ${d.county_seat}.`,
    extends: (d) => ` Partes de ${d.name} se extienden al condado de ${d.other_counties.join(", ")} — presente en el condado donde vive o donde surgió el caso.`,
    confirm: " Confirme las tarifas, la presentación electrónica y la dirección del tribunal con el Poder Judicial de Carolina del Norte antes de ir.",
    courthouse: (c) => `Tribunal del condado de ${c} en nccourts.gov`, popular: (n) => `Formularios populares para residentes de ${n}`,
    faq: (n) => `Preguntas sobre trámites en ${n}`, nearby: (c) => `Otros lugares del condado de ${c}`, allCounty: (c) => `Todo el condado de ${c} →`,
    disclaimer: (n) => `NC Legal Forms no está afiliado al Poder Judicial de Carolina del Norte ni al municipio de ${n} y no ofrece asesoría legal.`,
  },
};

const Seo = ({ data, url, faqs, slug, city, lang, base }) => {
  const es = lang === "es";
  const S = PLACE_SEO[lang];
  const title = S ? S.cityTitle(data) : es
    ? `Formularios judiciales de ${data.name}, NC — Llene, imprima y presente en línea | NC Legal Forms`
    : `${data.name}, NC Court Forms — Fill, Print & File Online | NC Legal Forms`;
  const desc = S ? S.cityDesc(data) : es
    ? `Formularios judiciales oficiales de Carolina del Norte para residentes de ${data.name}. Se presentan ante el Secretario del Tribunal Superior del condado de ${data.county} en ${data.county_seat}. Llénelos en línea con IA e imprima o descargue PDFs.`
    : `Official North Carolina court forms for ${data.name} residents. Filed with the ${data.county} County Clerk of Superior Court in ${data.county_seat}. Fill online with AI, then print or download court-ready PDFs.`;
  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {altLinks(SITE, `/${slug}/${city}`).map((l) => <link key={l.hrefLang} rel="alternate" hrefLang={l.hrefLang} href={l.href} />)}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "North Carolina Counties", item: `${SITE}${base}` },
          { "@type": "ListItem", position: 2, name: `${data.county} County`, item: `${SITE}${base}/${slug}` },
          { "@type": "ListItem", position: 3, name: data.name, item: url },
        ],
      })}</script>
      {faqs.length > 0 && (
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        })}</script>
      )}
    </Helmet>
  );
};

export default function CityPage() {
  const { slug, city } = useParams();
  const { lang, base, ready } = usePlaceLang();
  useEffect(() => { if (ready) trackView({ kind: "city", lang, county: slug, city }); }, [ready, lang, slug, city]);
  const L = pageText(lang, "city") || (lang === "es" ? T.es : T.en);
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/places/cities/${slug}/${city}`, { params: { lang } }).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [slug, city, lang]);

  if (missing) return <Navigate to={`${base}/${slug}`} replace />;
  if (!data) return (<div><Navbar /><div className="min-h-[60vh] grid place-items-center"><p className="overline text-muted-foreground animate-pulse">{L.loading}</p></div></div>);

  const g = data.guide || {};
  const faqs = g.faqs || [];
  const url = `${SITE}${base}/${slug}/${city}`;

  return (
    <div>
      <Seo data={data} url={url} faqs={faqs} slug={slug} city={city} lang={lang} base={base} />
      <Navbar />

      <section className="max-w-5xl mx-auto px-6 pt-14 pb-10" data-testid="city-page">
        <nav className="text-sm text-muted-foreground mb-6 flex flex-wrap items-center gap-2" data-testid="city-breadcrumb">
          <Link to={base} className="hover:text-accent">{L.nc}</Link><span>/</span>
          <Link to={`${base}/${slug}`} className="hover:text-accent">{L.county(data.county)}</Link><span>/</span>
          <span className="text-foreground">{data.name}</span>
        </nav>
        <p className="overline text-accent mb-3 flex items-center gap-2"><MapPin size={14} /> {L.kind(KIND_TR[lang]?.[data.type] || data.type, data.county)}</p>
        <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6" data-testid="city-title">{L.h1(data.name)}</h1>
        <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed" data-testid="city-intro">{g.intro || L.whereBody(data)}</p>
        <div className="flex flex-wrap gap-3 mt-8">
          <Link to="/find"><Button className="rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="city-finder-btn"><Sparkles size={16} /> {L.find}</Button></Link>
          <Link to="/forms"><Button variant="outline" className="rounded-sm gap-2 hover:bg-secondary" data-testid="city-browse-btn">{L.browse} <ArrowRight size={16} /></Button></Link>
        </div>
      </section>

      {(g.local_notes || []).length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-6" data-testid="city-local-notes">
          <h2 className="font-serif text-3xl tracking-tight mb-5">{L.tips(data.name)}</h2>
          <ul className="space-y-3 max-w-3xl">
            {g.local_notes.map((n) => (
              <li key={n} className="flex items-start gap-3 text-muted-foreground"><CheckCircle2 size={18} className="text-accent shrink-0 mt-0.5" />{n}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid-card p-8">
          <Landmark className="text-accent mb-3" />
          <h2 className="font-serif text-2xl tracking-tight mb-2">{L.where(data.name)}</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            {L.whereBody(data)}{data.other_counties?.length > 0 && L.extends(data)}{L.confirm}
          </p>
          <a href={`https://www.nccourts.gov/locations?county=${encodeURIComponent(data.county)}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 text-accent hover:underline font-medium" data-testid="city-official-link">
            {L.courthouse(data.county)} <ExternalLink size={15} />
          </a>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-6" data-testid="city-top-forms">
        <h2 className="font-serif text-3xl tracking-tight mb-6">{L.popular(data.name)}</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {topForms.map((f) => (
            <Link key={f.number} to={`/forms/${f.number}`} data-testid={`city-form-${f.number}`} className="grid-card p-4 flex items-center justify-between gap-3 group">
              <div className="min-w-0">
                <p className="text-[0.7rem] uppercase tracking-wider text-accent font-semibold">{f.topic}</p>
                <p className="font-serif text-base leading-tight truncate">{f.title}</p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">{f.number}</p>
              </div>
              <FileText size={18} className="text-muted-foreground shrink-0 group-hover:text-accent transition-colors" />
            </Link>
          ))}
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-6" data-testid="city-faq">
          <h2 className="font-serif text-3xl tracking-tight mb-6 flex items-center gap-2"><HelpCircle className="text-accent" /> {L.faq(data.name)}</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f, i) => (
              <div key={i} className="grid-card p-6" data-testid={`city-faq-${i}`}>
                <h3 className="font-serif text-lg leading-snug mb-2">{f.q}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <LocalLawyers county={data.county} city={data.name} place={data.name} lang={lang} />

      {data.nearby?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-8" data-testid="city-nearby">
          <h2 className="font-serif text-2xl tracking-tight mb-4">{L.nearby(data.county)}</h2>
          <div className="flex flex-wrap gap-2">
            {data.nearby.map((n) => (
              <Link key={n.slug} to={`${base}/${slug}/${n.slug}`} data-testid={`city-nearby-${n.slug}`}
                className="border border-border px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors">{n.name}</Link>
            ))}
            <Link to={`${base}/${slug}`} className="px-3 py-1.5 text-sm text-accent hover:underline">{L.allCounty(data.county)}</Link>
          </div>
        </section>
      )}

      <p className="max-w-5xl mx-auto px-6 pb-16 text-xs text-muted-foreground">{L.disclaimer(data.name)}</p>
      <Footer />
    </div>
  );
}
